import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IMarkdownEditorProps } from './IMarkdownEditorProps';
import { MarkdownView } from './MarkdownView';
import { mdStats } from './mdEdit';
import type { MdMode } from './MdMode';
import { MdToolbar } from './MdToolbar';
import { useMdEditor } from './useMdEditor';
import './markdown.css';

/**
 * Редактор статті Markdown (UIUX/field-types/rich-text; прототип kbEditor): рамка 12px, панель форматування, режими «Писати ·
 * Розділений · Перегляд» (вузьке вікно — «Писати»), поле Plex Mono 14px/1.75, перегляд зі справжнім виглядом статті й живими
 * запитами; підвал — слова, хвилини читання й підказка синтаксису. Лише читання — стаття без редактора.
 */
export function MarkdownEditor({ id, value, onChange, ws, label, readOnly, height, mode: initial }: IMarkdownEditorProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<MdMode>(initial ?? (typeof window !== 'undefined' && window.innerWidth < 1100 ? 'write' : 'split'));
  const ed = useMdEditor(value, onChange);
  const stats = mdStats(value);
  if (readOnly) return <MarkdownView text={value} ws={ws} emptyText={t('fieldControls.mdNothing')} className="md--field" />;
  const style = { height: height ?? 'max(360px, calc(100vh - 330px))' };
  return (
    <div className="md-editor">
      <MdToolbar ws={ws} mode={mode} onMode={setMode} wrap={ed.wrap} line={ed.line} insert={ed.insert} selected={ed.selected} />
      <div className="md-editor__body" style={style}>
        {mode !== 'preview' && (
          <textarea ref={ed.area} id={id} className="md-editor__text" aria-label={label} value={value} placeholder={t('fieldControls.mdPlaceholder')}
            spellCheck onChange={(e) => onChange(e.target.value)} />
        )}
        {mode !== 'write' && (
          <div className={mode === 'split' ? 'md-editor__preview md-editor__preview--split' : 'md-editor__preview'}>
            <MarkdownView text={value} ws={ws} emptyText={t('fieldControls.mdNothing')} />
          </div>
        )}
      </div>
      <div className="md-editor__foot">
        <span>{t('fieldControls.mdWords', { count: stats.words })}</span>
        <span>{t('fieldControls.mdRead', { count: stats.minutes })}</span>
        <span className="md-editor__spacer" />
        <span className="md-editor__syntax">{t('fieldControls.mdSyntax')}</span>
      </div>
    </div>
  );
}
